import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './features/auth/AuthContext';
import { SubscriptionProvider } from './features/subscription/SubscriptionContext';
import { ProtectedRoute } from './features/auth/ProtectedRoute';
import { Login } from './features/auth/Login';
import { SignUp } from './features/auth/SignUp';
import { AuthCallback } from './features/auth/AuthCallback';
import { LandingPage } from './features/landing/LandingPage';
import { PricingPage } from './features/pricing/PricingPage';
import { FAQPage } from './features/faq/FAQPage';
import { AboutPage } from './features/company/AboutPage';
import { CareersPage } from './features/company/CareersPage';
import { ContactPage } from './features/company/ContactPage';
import { PrivacyPage } from './features/legal/PrivacyPage';
import { TermsPage } from './features/legal/TermsPage';
import { SimulationWorkspace } from './features/editor/SimulationWorkspace';
import { SimulationsList } from './features/simulations/SimulationsList';
import { SimulationDetail } from './features/simulations/SimulationDetail';
import { useThemeStore } from './store/themeStore';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NotificationContainer } from './components/NotificationContainer';
import { ConfirmationContainer } from './components/ConfirmationContainer';
import { ConsentBanner } from './components/ConsentBanner';
import { AppHeader, WorkspaceNavigation } from './components/layout/AppLayout';
import { errorService } from './services/errorService';
import { clarityService } from './services/clarityService';
import { analyticsService } from './services/analyticsService';
import { useConsentStore } from './store/consentStore';

const AppContent = () => {
  const { user } = useAuth();
  const theme = useThemeStore((state) => state.theme);
  const location = useLocation();
  
  // Check if we're on a marketing page (no app header)
  const isMarketingPage = [
    '/', '/pricing', '/faq', '/login', '/signup', '/auth/callback',
    '/about', '/careers', '/contact',
    '/privacy', '/terms'
  ].includes(location.pathname);

  // Scroll to top on route change (but not if there's a hash)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  // Track page views in Google Analytics
  useEffect(() => {
    analyticsService.pageView(location.pathname, document.title);
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.style.setProperty('color-scheme', theme === 'dark' ? 'dark' : 'light');
    document.body.dataset.theme = theme;
  }, [theme]);

  // Add class to body and root for marketing pages to enable scrolling
  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (isMarketingPage) {
      document.body.classList.add('marketing-page-active');
      document.documentElement.classList.add('marketing-page-active');
      if (rootElement) {
        rootElement.classList.add('marketing-page-active');
      }
    } else {
      document.body.classList.remove('marketing-page-active');
      document.documentElement.classList.remove('marketing-page-active');
      if (rootElement) {
        rootElement.classList.remove('marketing-page-active');
      }
    }
  }, [isMarketingPage]);

  return (
    <div className="app-shell">
      {!isMarketingPage && <AppHeader />}
      <main className={isMarketingPage ? '' : 'app-main'}>
        {user && !isMarketingPage && <WorkspaceNavigation />}
        <div className={user && !isMarketingPage ? 'app-main-content' : undefined}>
          <Routes>
            {/* Marketing Pages - Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />

            {/* Auth Pages - Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* App Pages - Protected */}
            <Route
              path="/workspace"
              element={
                <ProtectedRoute>
                  <SimulationWorkspace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/simulations"
              element={
                <ProtectedRoute>
                  <SimulationsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/simulations/:simulationId"
              element={
                <ProtectedRoute>
                  <SimulationDetail />
                </ProtectedRoute>
              }
            />

            {/* Catch-all redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

function App() {
  const { preferences, status } = useConsentStore();

  // Initialize error service
  useEffect(() => {
    const env = (import.meta as { env?: { MODE?: string } }).env;
    errorService.initialize({
      environment: env?.MODE || 'development'
    });
  }, []);

  // Initialize Clarity analytics
  useEffect(() => {
    clarityService.initialize();
  }, []);

  // Initialize Google Analytics
  useEffect(() => {
    analyticsService.initialize();
  }, []);

  // Apply stored consent on initial load and sync when consent changes
  useEffect(() => {
    if (status === 'accepted' || status === 'rejected') {
      analyticsService.updateConsent(preferences.analytics, preferences.marketing);
      clarityService.updateConsent(preferences.analytics);
    }
  }, [preferences, status]);

  return (
    <ErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <SubscriptionProvider>
            <AppContent />
            <NotificationContainer />
            <ConfirmationContainer />
            <ConsentBanner />
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
